import React from 'react';
import PropTypes from 'prop-types';
import localization from 'assets/localization/localization';
import DocumentView from 'components/sales/downloadDocView/documentView';
import {
  DivLayoutV2NestedSection,
  DivLayoutV2FieldGrid,
  DivLayoutV2SubCard,
} from 'components/UI';
import './ContractExtras.css';

const display = (val) =>
  val != null && val !== '' ? String(val) : localization.na;

const formatRupee = (val) => `₹ ${parseFloat(val || 0).toFixed(2)}`;

const getAddressFields = (addr) => [
  { heading: 'Registered Name', data: display(addr.registered_name) },
  { heading: 'Address Line 1', data: display(addr.address_line_1) },
  { heading: 'Address Line 2', data: display(addr.address_line_2) },
  { heading: localization.zone, data: display(addr.zone) },
  { heading: localization.area, data: display(addr.area) },
  { heading: localization.state, data: display(addr.state || addr.geo_state) },
  {
    heading: localization.city,
    data: display(addr.city_name || addr.city || addr.geo_city),
  },
  { heading: 'Pincode', data: display(addr.pincode ?? addr.geo_pin ?? addr.pin_code) },
  { heading: 'GST No', data: display(addr.gst_no || addr.client_gst) },
];

const RequirementsTable = ({ columns, rows, emptyLabel }) => {
  if (!rows?.length) {
    return <p className="site-detail-contract-empty mb-0">{emptyLabel || localization.na}</p>;
  }
  return (
    <div className="site-detail-requirements-table-wrap">
      <table className="site-detail-requirements-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id ?? index}>
              {columns.map((col) => (
                <td key={col.key}>{col.render(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

RequirementsTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      render: PropTypes.func.isRequired,
    })
  ).isRequired,
  rows: PropTypes.array,
  emptyLabel: PropTypes.string,
};

const HardwareSection = ({ hardware }) => {
  const materials = hardware?.material || [];
  const categories = hardware?.category || [];

  if (!materials.length && !categories.length) return null;

  const columns = [
    { key: 'category', label: 'Category', render: (r) => display(r.category_name) },
    { key: 'material', label: 'Material', render: (r) => display(r.material_name) },
    { key: 'brand', label: 'Brand', render: (r) => display(r.brand_name) },
    { key: 'code', label: 'Code', render: (r) => display(r.material_code) },
    { key: 'qty', label: 'Qty', render: (r) => display(r.quantity) },
    { key: 'unit', label: 'Sale Unit Price', render: (r) => formatRupee(r.sale_unit_price) },
    { key: 'total', label: 'Item Price', render: (r) => formatRupee(r.item_price) },
  ];

  return (
    <DivLayoutV2NestedSection title="Hardware Requirements">
      {categories.length > 0 && (
        <DivLayoutV2FieldGrid
          fields={categories.map((cat, i) => ({
            key: `cat-${i}`,
            heading: cat.category_name || `Category ${i + 1}`,
            data: display(cat.quantity ?? cat.qty),
          }))}
        />
      )}
      <RequirementsTable columns={columns} rows={materials} />
    </DivLayoutV2NestedSection>
  );
};

const SOFTWARE_COLUMNS = [
  { key: 'service', label: 'Service', render: (r) => display(r.service_name) },
  { key: 'code', label: 'Service Code', render: (r) => display(r.service_code) },
  { key: 'qty', label: 'Qty', render: (r) => display(r.quantity) },
  { key: 'base', label: 'Base Unit Price', render: (r) => formatRupee(r.item_unit_price) },
  { key: 'sale', label: 'Sale Unit Price', render: (r) => formatRupee(r.sale_unit_price) },
  { key: 'spec', label: localization.specification, render: (r) => display(r.specification) },
];

const TAGS_COLUMNS = [
  { key: 'name', label: 'Tag', render: (r) => display(r.name) },
  { key: 'status', label: 'Payment Status', render: (r) => display(r.status) },
  { key: 'qty', label: 'Qty', render: (r) => display(r.qty) },
  { key: 'receive', label: 'Receive Qty', render: (r) => display(r.receive_qty) },
  { key: 'price', label: 'Price', render: (r) => formatRupee(r.price) },
  { key: 'code', label: 'Material Code', render: (r) => display(r.material_code) },
  { key: 'remark', label: localization.specification, render: (r) => display(r.remark) },
];

const SoftwareSection = ({ software }) => {
  if (!software?.length) return null;

  return (
    <DivLayoutV2NestedSection title="Software Requirements">
      <RequirementsTable columns={SOFTWARE_COLUMNS} rows={software} />
    </DivLayoutV2NestedSection>
  );
};

const TagsSection = ({ tags }) => {
  if (!tags?.length) return null;

  return (
    <DivLayoutV2NestedSection title={localization.tags || 'Tags'}>
      <RequirementsTable columns={TAGS_COLUMNS} rows={tags} />
    </DivLayoutV2NestedSection>
  );
};

const ClientSection = ({ clientDetails }) => {
  if (!clientDetails?.client && !clientDetails?.addresses?.length) return null;

  const client = clientDetails.client || {};

  return (
    <DivLayoutV2NestedSection title="Client Details">
      <DivLayoutV2FieldGrid
        fields={[
          { heading: 'Client Name', data: display(client.client_name) },
          { heading: 'PAN', data: display(client.pan_number) },
          { heading: 'Recon Client Id', data: display(client.recon_client_id) },
          { heading: 'TAN', data: display(client.tan_number) },
        ]}
      />
      {(clientDetails.addresses || []).map((addr, index) => (
        <DivLayoutV2SubCard
          key={addr.id ?? index}
          badge={clientDetails.addresses.length > 1 ? `Client Address ${index + 1}` : 'Client Address'}
          fields={getAddressFields(addr)}
        />
      ))}
    </DivLayoutV2NestedSection>
  );
};

const LeadAddressesSection = ({ addresses }) => {
  if (!addresses?.length) return null;

  return (
    <DivLayoutV2NestedSection title="Lead Address">
      {addresses.map((addr, index) => (
        <DivLayoutV2SubCard
          key={addr.id ?? index}
          badge={addresses.length > 1 ? `Lead Address ${index + 1}` : 'Lead Address'}
          fields={[
            ...getAddressFields(addr),
            { heading: 'Latitude', data: display(addr.latitude) },
            { heading: 'Longitude', data: display(addr.longitude) },
          ]}
        />
      ))}
    </DivLayoutV2NestedSection>
  );
};

const AgreementSection = ({ documents }) => {
  const urls = Array.isArray(documents) ? documents.filter(Boolean) : [];
  if (!urls.length) return null;

  return (
    <DivLayoutV2NestedSection title="Agreement Documents">
      {urls.map((url, index) => (
        <DocumentView
          key={url}
          title={`Agreement ${urls.length > 1 ? index + 1 : ''}`.trim()}
          attachment={url}
        />
      ))}
    </DivLayoutV2NestedSection>
  );
};

const ContractExtras = ({ contract }) => {
  if (!contract) return null;

  const hardware = contract.hardware_requirements;
  const software = contract.software_requirements;
  const tags = contract.lead_tags;
  const clientDetails = contract.client_details;
  const leadAddresses = contract.lead_addresses;
  const agreements = contract.agreement_document;

  const hasAny =
    hardware?.material?.length ||
    hardware?.category?.length ||
    software?.length ||
    tags?.length ||
    clientDetails?.client ||
    clientDetails?.addresses?.length ||
    leadAddresses?.length ||
    agreements?.length;

  if (!hasAny) return null;

  return (
    <>
      <HardwareSection hardware={hardware} />
      <SoftwareSection software={software} />
      <TagsSection tags={tags} />
      <ClientSection clientDetails={clientDetails} />
      <LeadAddressesSection addresses={leadAddresses} />
      <AgreementSection documents={agreements} />
    </>
  );
};

ContractExtras.propTypes = {
  contract: PropTypes.object.isRequired,
};

export default ContractExtras;
